import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth } from '../lib/adminApi';

/**
 * "Upcoming filings" calendar — every customer we've filed for, showing
 * when their next AA02 is due at Companies House. Sorted overdue-first,
 * then by due date ascending.
 *
 * Purpose: drive repeat business. Dormant accounts are annual, so every
 * accepted customer needs to file again next year. Surfacing them here
 * lets the operator proactively email a reminder ~60 days before due,
 * rather than waiting for the customer to remember.
 *
 * Data is fetched live from Companies House on page load (no caching yet
 * — add if the operator hits latency at scale). Failed lookups sink to
 * the bottom so the calendar stays useful even when CH is flaky.
 */

type Row = {
  companyNumber: string;
  companyName: string;
  email: string;
  lastPeriodEnd: string;
  nextPeriodEnd: string | null;
  nextDueOn: string | null;
  overdue: boolean;
  lookupFailed: boolean;
};

const BUCKETS = [
  { key: 'overdue', label: 'Overdue', from: -Infinity, to: 0 },
  { key: '30', label: 'Due in next 30 days', from: 0, to: 30 },
  { key: '60', label: 'Due in 31–60 days', from: 31, to: 60 },
  { key: '90', label: 'Due in 61–90 days', from: 61, to: 90 },
  { key: 'later', label: 'Due later than 90 days', from: 91, to: Infinity },
  { key: 'unknown', label: 'Could not fetch CH data', from: null, to: null },
] as const;

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(+d)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const ms = d.getTime() - now.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AdminUpcoming() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    adminApi
      .upcomingFilings()
      .then((r) => {
        if (alive) setRows(r.items);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (r) =>
        r.companyName.toLowerCase().includes(query) ||
        r.companyNumber.toLowerCase().includes(query) ||
        r.email.toLowerCase().includes(query),
    );
  }, [rows, q]);

  const grouped = useMemo(() => {
    if (!filtered) return null;
    const g: Record<string, Row[]> = {};
    for (const b of BUCKETS) g[b.key] = [];
    for (const r of filtered) {
      if (r.lookupFailed) {
        g.unknown.push(r);
        continue;
      }
      const d = daysUntil(r.nextDueOn);
      if (d === null) {
        g.unknown.push(r);
        continue;
      }
      if (d < 0) g.overdue.push(r);
      else if (d <= 30) g['30'].push(r);
      else if (d <= 60) g['60'].push(r);
      else if (d <= 90) g['90'].push(r);
      else g.later.push(r);
    }
    return g;
  }, [filtered]);

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Upcoming filings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every customer we've filed for, sorted by when their next AA02 is
          due at Companies House. Overdue first. Use this to send
          proactive reminders before the deadline.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by company, number, or email…"
          className="input max-w-md flex-1"
        />
        {loading && <span className="text-xs text-slate-400">Loading from Companies House…</span>}
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {!loading && rows && rows.length === 0 && (
        <div className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-600 ring-1 ring-slate-200">
          No accepted filings yet. Once your first customer's filing is
          accepted, they'll appear here with the date their next accounts
          are due.
        </div>
      )}

      {grouped && (
        <div className="space-y-6">
          {BUCKETS.map((b) => {
            const items = grouped[b.key];
            if (!items || items.length === 0) return null;
            const isOverdue = b.key === 'overdue';
            const isUnknown = b.key === 'unknown';
            return (
              <section key={b.key}>
                <h2
                  className={`mb-2 text-xs font-semibold uppercase tracking-wide ${
                    isOverdue
                      ? 'text-red-700'
                      : isUnknown
                        ? 'text-slate-400'
                        : 'text-slate-600'
                  }`}
                >
                  {b.label} · {items.length}
                </h2>
                <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2.5">Company</th>
                        <th className="px-4 py-2.5">Customer</th>
                        <th className="px-4 py-2.5">Last filed period end</th>
                        <th className="px-4 py-2.5">Next period end</th>
                        <th className="px-4 py-2.5">Due on</th>
                        <th className="px-4 py-2.5">Days</th>
                        <th className="px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((r) => {
                        const d = daysUntil(r.nextDueOn);
                        return (
                          <tr key={`${r.companyNumber}-${r.email}`} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{r.companyName}</div>
                              <div className="text-xs text-slate-500">{r.companyNumber}</div>
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              <a
                                href={`mailto:${r.email}`}
                                className="text-brand-700 hover:underline"
                              >
                                {r.email}
                              </a>
                            </td>
                            <td className="px-4 py-3 text-slate-700">{formatDate(r.lastPeriodEnd)}</td>
                            <td className="px-4 py-3 text-slate-700">{formatDate(r.nextPeriodEnd)}</td>
                            <td
                              className={`px-4 py-3 ${
                                r.overdue ? 'font-semibold text-red-700' : 'text-slate-700'
                              }`}
                            >
                              {formatDate(r.nextDueOn)}
                            </td>
                            <td
                              className={`px-4 py-3 tabular-nums ${
                                r.overdue
                                  ? 'font-semibold text-red-700'
                                  : d !== null && d <= 30
                                    ? 'font-semibold text-amber-700'
                                    : 'text-slate-500'
                              }`}
                            >
                              {d === null ? '—' : d < 0 ? `${Math.abs(d)}d late` : `${d}d`}
                            </td>
                            <td className="px-4 py-3">
                              <Link
                                to={`/admin/customers?q=${encodeURIComponent(r.email)}`}
                                className="text-xs text-brand-700 hover:underline"
                              >
                                Open customer →
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
